import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateReminder } from 'src/Hooks/Mutations/useCreateReminder';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchMemberReminders from 'src/Hooks/Queries/useFetchMemberReminders';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import useToneTemplate from 'src/Hooks/Shared/useToneTemplate';
import {
  CreateMemberReminderDtoRecurrenceEnum,
  CreateMemberReminderDtoScheduleEnum,
  CreateMemberReminderDtoToneEnum,
} from 'src/Services/API/generated';

import styles from './CreateReminderModal.module.scss';
import { Props } from './CreateReminderModal.types';

const CreateReminderModal = ({ open, onClose, assetMemberId, memberName, netBalance }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data: asset } = useFetchAssetDetails();
  const { data: gestionnaire } = useFetchUser();
  const { data: history } = useFetchMemberReminders(open ? assetMemberId : undefined);
  const { mutate, isLoading } = useCreateReminder({ onSuccess: onClose });

  const [tone, setTone] = useState<CreateMemberReminderDtoToneEnum>(
    CreateMemberReminderDtoToneEnum.Amical
  );
  const [schedule, setSchedule] = useState<CreateMemberReminderDtoScheduleEnum>(
    CreateMemberReminderDtoScheduleEnum.Immediate
  );
  const [daysFromNow, setDaysFromNow] = useState(7);
  const [recurrence, setRecurrence] = useState<CreateMemberReminderDtoRecurrenceEnum>(
    CreateMemberReminderDtoRecurrenceEnum.Monthly
  );
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(true);

  const gestionnaireName = gestionnaire ? `${gestionnaire.firstName} ${gestionnaire.lastName}` : '';
  const assetName = asset?.name ?? '';

  const templateInput = useMemo(
    () => ({ memberName, amount: netBalance, gestionnaireName, assetName }),
    [memberName, netBalance, gestionnaireName, assetName]
  );
  const template = useToneTemplate(tone, templateInput);

  const [subject, setSubject] = useState(template.subject);
  const [message, setMessage] = useState(template.message);

  useEffect(() => {
    if (open) {
      setSubject(template.subject);
      setMessage(template.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tone]);

  const handleSave = () => {
    if (!assetId) return;
    const scheduledAt =
      schedule === CreateMemberReminderDtoScheduleEnum.Immediate
        ? undefined
        : dayjs().add(daysFromNow, 'day').toISOString();

    mutate({
      assetId,
      data: {
        targetMemberId: assetMemberId,
        tone,
        subject,
        message,
        schedule,
        scheduledAt,
        recurrence:
          schedule === CreateMemberReminderDtoScheduleEnum.Recurring ? recurrence : undefined,
        channels: { email: emailEnabled, push: pushEnabled },
      },
    });
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t('reminders.create.title', 'Programmer une relance')}
      size="lg"
    >
      <Stack gap="md">
        <Text size="sm" fw={600}>
          {memberName}
        </Text>

        <div>
          <Text size="sm" fw={500} mb="xs">
            {t('reminders.create.tone', 'Ton')}
          </Text>
          <SegmentedControl
            fullWidth
            value={tone}
            onChange={value => setTone(value as CreateMemberReminderDtoToneEnum)}
            data={[
              { value: CreateMemberReminderDtoToneEnum.Amical, label: 'Amical' },
              { value: CreateMemberReminderDtoToneEnum.Neutre, label: 'Neutre' },
              { value: CreateMemberReminderDtoToneEnum.Ferme, label: 'Ferme' },
            ]}
          />
        </div>

        <TextInput
          label={t('reminders.create.subject', 'Objet')}
          value={subject}
          onChange={e => setSubject(e.currentTarget.value)}
        />
        <Textarea
          label={t('reminders.create.message', 'Message')}
          minRows={6}
          value={message}
          onChange={e => setMessage(e.currentTarget.value)}
        />

        <div>
          <Text size="sm" fw={500} mb="xs">
            {t('reminders.create.schedule', "Date d'envoi")}
          </Text>
          <SegmentedControl
            fullWidth
            value={schedule}
            onChange={value => setSchedule(value as CreateMemberReminderDtoScheduleEnum)}
            data={[
              { value: CreateMemberReminderDtoScheduleEnum.Immediate, label: 'Maintenant' },
              { value: CreateMemberReminderDtoScheduleEnum.Scheduled, label: 'Dans X jours' },
              { value: CreateMemberReminderDtoScheduleEnum.Recurring, label: 'Récurrent' },
            ]}
          />
        </div>

        {schedule !== CreateMemberReminderDtoScheduleEnum.Immediate && (
          <NumberInput
            label={t('reminders.create.daysFromNow', 'Dans combien de jours ?')}
            value={daysFromNow}
            onChange={value => setDaysFromNow(typeof value === 'number' ? value : 1)}
            min={1}
          />
        )}

        {schedule === CreateMemberReminderDtoScheduleEnum.Recurring && (
          <SegmentedControl
            fullWidth
            value={recurrence}
            onChange={value => setRecurrence(value as CreateMemberReminderDtoRecurrenceEnum)}
            data={[
              { value: CreateMemberReminderDtoRecurrenceEnum.Monthly, label: 'Mensuel' },
              { value: CreateMemberReminderDtoRecurrenceEnum.Quarterly, label: 'Trimestriel' },
            ]}
          />
        )}

        <div>
          <Text size="sm" fw={500} mb="xs">
            {t('reminders.create.channels', 'Canaux')}
          </Text>
          <Group>
            <Switch
              label="Email"
              checked={emailEnabled}
              onChange={e => setEmailEnabled(e.currentTarget.checked)}
            />
            <Switch
              label="Push"
              checked={pushEnabled}
              onChange={e => setPushEnabled(e.currentTarget.checked)}
            />
          </Group>
        </div>

        {history && history.length > 0 && (
          <Alert color="gray" variant="light">
            {t('reminders.create.history', '{{count}} relance(s) déjà envoyée(s) à ce membre.', {
              count: history.length,
            })}
          </Alert>
        )}

        <div className={styles.buttons}>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleSave} loading={isLoading} disabled={!emailEnabled && !pushEnabled}>
            {t('reminders.create.save', 'Programmer')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
};

export default CreateReminderModal;
